import type { ReactNode } from "react";
import { Info } from "lucide-react";

/**
 * Small inline "what does this mean?" hint.
 *
 * The price index publishes finance-style terms (basket, breadth, coverage,
 * base 1000) that a Palworld player has no reason to know, so every such label
 * carries one of these. Hover or keyboard focus reveals the plain-English
 * explanation; the text is always in the DOM so screen readers and crawlers
 * read it too.
 */
export function InfoHint({ label, children }: { label: string; children: ReactNode }) {
  return (
    <span className="group relative inline-flex items-center align-middle">
      <button
        type="button"
        aria-label={`What does ${label} mean?`}
        className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-white/45 hover:text-white focus-visible:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/60"
      >
        <Info className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 z-40 mb-2 w-[min(15rem,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-lg border border-white/15 bg-[hsl(222_47%_8%)] p-2.5 text-left text-[11px] font-normal leading-snug text-white/80 opacity-0 shadow-xl transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
      >
        <span className="block font-semibold text-white">{label}</span>
        {children}
      </span>
    </span>
  );
}
